import { get, send } from "./api.js";

export type ExamProjectStatus = "active" | "completed" | "withdrawn";
export type ExamDateKind = "tentative" | "confirmed";

export interface ExamProjectGoalLink {
  id: string;
  title: string;
  status: "active" | "achieved" | "paused" | "abandoned";
  targetDate?: string | null;
}

export interface ExamProjectRepertoireLink {
  id: string;
  title: string;
  composer: string | null;
  status: "learning" | "polishing" | "performance_ready" | "completed" | "paused";
}

export interface StudentExamProject {
  id: string;
  board: string | null;
  examType: string | null;
  grade: string | null;
  instrument: string | null;
  examDate: string | null;
  examDateKind: ExamDateKind;
  requirements: string | null;
  entryMilestone: string | null;
  result?: string | null;
  status: ExamProjectStatus;
  familyVisible?: boolean;
  familyResultVisible: boolean;
  revision?: number;
  linkedGoals: ExamProjectGoalLink[];
  linkedRepertoire: ExamProjectRepertoireLink[];
}

export interface FamilyStudentExamProject extends StudentExamProject {
  result?: string | null;
  status: ExamProjectStatus;
}

export interface ExamProjectInput {
  board?: string | null;
  examType?: string | null;
  grade?: string | null;
  instrument?: string | null;
  examDate?: string | null;
  examDateKind?: ExamDateKind;
  requirements?: string | null;
  entryMilestone?: string | null;
  result?: string | null;
  status?: ExamProjectStatus;
  familyVisible?: boolean;
  familyResultVisible?: boolean;
  goalIds?: string[];
  repertoireIds?: string[];
}

function staffPath(studentId: string): string {
  return `/admin/students/${encodeURIComponent(studentId)}/exam-projects`;
}

function familyPath(studentId: string): string {
  return `/me/students/${encodeURIComponent(studentId)}/exam-projects`;
}

export async function getStaffStudentExamProjects(studentId: string): Promise<StudentExamProject[]> {
  const response = await get<{ projects: StudentExamProject[] }>(staffPath(studentId));
  return response.projects;
}

export async function createStudentExamProject(
  studentId: string,
  input: ExamProjectInput,
): Promise<StudentExamProject> {
  const response = await send<{ project: StudentExamProject }>("POST", staffPath(studentId), input);
  return response.project;
}

export async function updateStudentExamProject(
  studentId: string,
  projectId: string,
  input: ExamProjectInput & { revision: number },
): Promise<StudentExamProject> {
  const response = await send<{ project: StudentExamProject }>(
    "PATCH",
    `${staffPath(studentId)}/${encodeURIComponent(projectId)}`,
    input,
  );
  return response.project;
}

export async function getFamilyStudentExamProjects(
  studentId: string,
): Promise<FamilyStudentExamProject[]> {
  const response = await get<{ projects: FamilyStudentExamProject[] }>(familyPath(studentId));
  return response.projects;
}
